

import { Activity, ActualActivity, Phase, Program, PhaseHoursBreakdown } from "@/types";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { PhaseHoursCalculator } from "@/services/PhaseHoursCalculator";


@Component({
    components: {
        AddActualActivityDialog: () => import('@/components/dialogs/AddActualActivity.vue'),
        EditActualActivity: () => import('@/components/dialogs/EditActualActivity.vue'),
    }
})
export default class ProgramProgressChart extends Vue {
    @Prop({ required: true }) program!: Program;
    @Prop({ required: true }) phases!: Phase[];
    @Prop({ required: true }) activities!: Activity[];
    @Prop({ required: true }) actualActivities!: ActualActivity[];

    open = false;
    tab = 'total';
    
    // Cache for calculated hours
    actualHoursCache: { [key: string]: PhaseHoursBreakdown } = {};
    cacheVersion = 0;
    isLoadingStats = true;

    async mounted() {
        await this.calculateAllStats();
    }

    @Watch('phases')
    @Watch('activities')
    @Watch('actualActivities')
    async onDataChange() {
        await this.calculateAllStats();
    }

    async calculateAllStats() {
        this.isLoadingStats = true;
        const newCache: { [key: string]: PhaseHoursBreakdown } = {};
        try {
            // Calculate for each phase
            for (const phase of this.phases) {
                const actual = await PhaseHoursCalculator.calculateActualHours(
                    phase,
                    this.program.participant,
                    this.activities,
                    this.actualActivities
                );
                newCache[phase.id!] = actual;
            }
            // Replace whole object so Vue 2 detects the change reactively
            this.actualHoursCache = newCache;
            // Bump version so all computed properties that read from the cache re-evaluate.
            // Vue 2 won't track dynamic key reads on an empty object, so this is needed.
            this.cacheVersion++;
        } catch (error) {
            console.error('Error calculating hours:', error);
        } finally {
            this.isLoadingStats = false;
        }
    }

    get totalPlannedStats(): PhaseHoursBreakdown {
        return this.phases.reduce((sum, phase) => {
            const planned = PhaseHoursCalculator.getPlannedHours(phase, this.activities);
            return {
                activityHours: sum.activityHours + planned.activityHours,
                screencastHours: sum.screencastHours + planned.screencastHours,
                webinarHours: sum.webinarHours + planned.webinarHours,
            };
        }, { activityHours: 0, screencastHours: 0, webinarHours: 0 });
    }

    get totalActualStats(): PhaseHoursBreakdown {
        return Object.values(this.actualHoursCache).reduce((sum, actual) => ({
            activityHours: sum.activityHours + actual.activityHours,
            screencastHours: sum.screencastHours + actual.screencastHours,
            webinarHours: sum.webinarHours + actual.webinarHours,
        }), { activityHours: 0, screencastHours: 0, webinarHours: 0 });
    }

    // Calculate hours for activities (including hours registered when participant was not present)
    getPerformedHours(phaseId?: string): number {
        const relevantActivities = phaseId 
            ? this.activities.filter(a => a.phaseId === phaseId)
            : this.activities;
        
        return relevantActivities.reduce((sum, activity) => {
            const actuals = this.actualActivities.filter(
                aa => aa.activityId === activity.id
            );
            return sum + actuals.reduce((s, aa) => s + aa.actualHours, 0);
        }, 0);
    }

    // Calculate hours for activities where participant confirmed
    getConfirmedHours(phaseId?: string): number {
        const relevantActivities = phaseId 
            ? this.activities.filter(a => a.phaseId === phaseId)
            : this.activities;
        
        return relevantActivities.reduce((sum, activity) => {
            const actuals = this.actualActivities.filter(
                aa => aa.activityId === activity.id && aa.participantConfirmed === true
            );
            return sum + actuals.reduce((s, aa) => s + aa.actualHours, 0);
        }, 0);
    }

    // Calculate planned hours for a phase
    getPlannedHours(phaseId?: string): number {
        const relevantActivities = phaseId 
            ? this.activities.filter(a => a.phaseId === phaseId)
            : this.activities;
        
        return relevantActivities.reduce((sum, activity) => sum + activity.plannedHours, 0);
    }

    statsForPhase(phaseIndex: number) {
        const phase = this.phases[phaseIndex];
        if (!phase) {
            return {
                planned: { activityHours: 0, screencastHours: 0, webinarHours: 0 },
                actual: { activityHours: 0, screencastHours: 0, webinarHours: 0 }
            };
        }
        return {
            planned: PhaseHoursCalculator.getPlannedHours(phase, this.activities),
            actual: this.actualHoursCache[phase.id!] || { activityHours: 0, screencastHours: 0, webinarHours: 0 }
        };
    }

    get currentStats() {
        // Depend on cacheVersion so this re-evaluates after async cache population
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.cacheVersion;
        if (this.tab === 'total') {
            return {
                planned: this.totalPlannedStats,
                actual: this.totalActualStats
            };
        } else {
            return this.statsForPhase(parseInt(this.tab) - 1);
        }
    }

    // True when the current tab's phase(s) use the new template-based system
    get isCurrentTabNewSystem(): boolean {
        if (this.tab === 'total') {
            return this.phases.some(p => !!(p.hours || p.screencastHours || p.webinarHours));
        } else {
            const phase = this.phases[parseInt(this.tab) - 1];
            return !!(phase && (phase.hours || phase.screencastHours || phase.webinarHours));
        }
    }

    get totalPlanned(): number {
        if (this.tab === 'total') {
            return this.getPlannedHours();
        } else {
            const phaseIndex = parseInt(this.tab) - 1;
            return this.getPlannedHours(this.phases[phaseIndex].id);
        }
    }

    get totalPerformed(): number {
        if (this.tab === 'total') {
            return this.getPerformedHours();
        } else {
            const phaseIndex = parseInt(this.tab) - 1;
            return this.getPerformedHours(this.phases[phaseIndex].id);
        }
    }

    get totalConfirmed(): number {
        if (this.tab === 'total') {
            return this.getConfirmedHours();
        } else {
            const phaseIndex = parseInt(this.tab) - 1;
            return this.getConfirmedHours(this.phases[phaseIndex].id);
        }
    }

    get confirmationPercentage(): number {
        if (this.totalPlanned === 0) return 0;
        return Math.min(Math.round((this.totalConfirmed / this.totalPlanned) * 100), 100);
    }

    get grandTotalPlanned(): number {
        const p = this.currentStats.planned;
        return p.activityHours + p.screencastHours + p.webinarHours;
    }

    get grandTotalActual(): number {
        const a = this.currentStats.actual;
        return a.activityHours + a.screencastHours + a.webinarHours;
    }

    get grandConfirmationPercentage(): number {
        if (this.grandTotalPlanned === 0) return 0;
        return Math.min(Math.round((this.grandTotalActual / this.grandTotalPlanned) * 100), 100);
    }

    // The radial chart tracks activities only — screencasts/webinars are shown in the
    // header breakdown but excluded from the planned/performed/confirmed rings.
    get chartPlannedActivities(): number {
        return this.currentStats.planned.activityHours;
    }

    get chartPerformed(): number {
        return this.totalPerformed;
    }

    get chartConfirmed(): number {
        return this.totalConfirmed;
    }

    round2(n: number): number {
        return Math.round(n * 100) / 100;
    }

    getSeries() {
        const planned = this.chartPlannedActivities;
        const performed = this.chartPerformed;
        const confirmed = this.chartConfirmed;
        
        if (planned === 0) return [0, 0, 0];
        
        return [
            100,
            Math.min(Math.round((performed / planned) * 100), 100),
            Math.min(Math.round((confirmed / planned) * 100), 100)
        ];
    }

    get series() {
        return this.getSeries();
    }

    get chartOptions() {
        return {
            chart: {
                height: 390,
                type: 'radialBar',
            },
            plotOptions: {
                radialBar: {
                    offsetY: 0,
                    startAngle: 0,
                    endAngle: 270,
                    hollow: {
                        margin: 5,
                        size: '30%',
                        background: 'transparent',
                        image: undefined,
                    },
                    dataLabels: {
                        name: {
                            show: false,
                        },
                        value: {
                            show: false,
                        }
                    },
                    barLabels: {
                        enabled: true,
                        useSeriesColors: true,
                        offsetX: -8,
                        fontSize: '16px',
                        formatter: (seriesName: any, opts: any) => {
                            let hours = 0;
                            
                            if (opts.seriesIndex === 0) {
                                hours = this.chartPlannedActivities;
                            } else if (opts.seriesIndex === 1) {
                                hours = this.chartPerformed;
                            } else if (opts.seriesIndex === 2) {
                                hours = this.chartConfirmed;
                            }
                            
                            return `${seriesName}: ${this.round2(hours)}h`
                        },
                    },
                }
            },
            colors: ['#1976D2', '#FF9800', '#4CAF50'],
            labels: [this.$t('planned'), this.$t('performed'), this.$t('confirmed')],
            responsive: [{
                breakpoint: 480,
                options: {
                    legend: {
                        show: false
                    }
                }
            }]
        }
    }
}
